/**
 * InlineText — Single-line counterpart to InlineNote.
 *
 * Display mode: text + top-right pencil that appears on hover.
 * Edit mode: input + Save / Cancel.
 * Empty state: "+ Add" placeholder button.
 *
 * S9-hotfix6 (#3 + #4):
 *   - Project Title and similar required-but-already-filled fields no longer
 *     show a permanent text input with a leading asterisk. Once a value is
 *     present we drop into the filled state and require an explicit edit click.
 *   - Pencil sits in the top-right corner so it doesn't consume an extra row.
 */

import { useEffect, useRef, useState } from 'react'

export interface InlineTextProps {
  value: string
  onChange: (newValue: string) => void
  placeholder?: string
  emptyLabel?: string
  required?: boolean
  editable?: boolean
  className?: string
  textClassName?: string
}

export function InlineText({
  value,
  onChange,
  placeholder = 'Type a value…',
  emptyLabel = '+ Add value',
  required = false,
  editable = true,
  className = '',
  textClassName = 'text-[13px] text-[#e8e8f0]',
}: InlineTextProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!isEditing) setDraft(value)
  }, [value, isEditing])

  useEffect(() => {
    if (isEditing && inputRef.current) inputRef.current.focus()
  }, [isEditing])

  const isEmpty = !value || value.trim() === ''

  const handleSave = () => {
    if (required && draft.trim() === '') return
    onChange(draft)
    setIsEditing(false)
  }
  const handleCancel = () => {
    setDraft(value)
    setIsEditing(false)
  }
  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      handleCancel()
    }
  }

  // Empty state — render a single dashed button.
  if (isEmpty && !isEditing) {
    return (
      <button
        type="button"
        onClick={() => editable && setIsEditing(true)}
        disabled={!editable}
        className={`
          w-full px-3 py-2 text-left
          border border-dashed border-[#2a2a44]
          rounded-lg
          text-[#a8a8c0] text-[13px]
          ${editable ? 'hover:border-[#3a3a54] hover:text-[#c8c8d8] cursor-pointer' : 'opacity-50 cursor-not-allowed'}
          ${className}
        `}
      >
        {emptyLabel}{required ? ' *' : ''}
      </button>
    )
  }

  // Edit mode
  if (isEditing) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKey}
          placeholder={placeholder}
          className="
            flex-1 px-3 py-2
            bg-[#12122a] border border-[#2a2a44]
            rounded-lg
            text-[#e8e8f0] text-[13px]
            placeholder:text-[#5a5a6c]
            focus:border-[#3a3a54] focus:ring-1 focus:ring-[#3a3a54]/50
          "
        />
        <button
          type="button"
          onClick={handleSave}
          disabled={required && draft.trim() === ''}
          className="
            px-3 py-1.5
            bg-[#E61B25] text-white
            rounded-md
            text-[12px] font-medium
            transition-colors hover:bg-[#d41620]
            disabled:opacity-50
          "
        >
          Save
        </button>
        <button
          type="button"
          onClick={handleCancel}
          className="
            px-3 py-1.5
            bg-transparent text-[#a8a8c0]
            rounded-md
            text-[12px] font-medium
            hover:text-[#c8c8d8] hover:bg-[#1a1a2e]
          "
        >
          Cancel
        </button>
      </div>
    )
  }

  // Display mode (filled)
  return (
    <div className={`relative group ${className}`}>
      <div className={`pr-7 break-words ${textClassName}`}>{value}</div>
      {editable && (
        <button
          type="button"
          onClick={() => setIsEditing(true)}
          className="
            absolute top-0 right-0
            p-1
            text-[12px] text-[#8b8ba0]
            opacity-0 group-hover:opacity-100 focus:opacity-100
            transition-opacity
            hover:text-[#e8e8f0]
          "
          aria-label="Edit"
          title="Edit"
        >
          ✎
        </button>
      )}
    </div>
  )
}

export default InlineText
